import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Zap, PartyPopper } from "lucide-react";
import { PageHeader, SectionTitle, Spinner } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import QuoteCalculator from "@/components/QuoteCalculator";
import { getPricingConfig, getFaqs, careSubscribe, paymentStatus } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

function PackageCard({ p, i }) {
  const { fmt } = useCurrency();
  return (
    <Reveal delay={i * 0.07} className="h-full">
      <div data-testid={`package-${p.id}`} className={`flex flex-col h-full border p-8 ${p.featured ? "border-primary bg-card soft-shadow-lg relative" : "border-border bg-card"}`}>
        {p.featured && <span className="absolute -top-3 left-8 font-mono text-[10px] uppercase tracking-widest bg-primary text-white px-3 py-1">Most popular</span>}
        <h3 className="font-display text-2xl">{p.name}</h3>
        <p className="mt-3 font-display text-4xl text-primary">
          {p.custom ? `${fmt(p.price_from)}+` : <>{fmt(p.price_from)}</>}
        </p>
        <p className="mt-1 text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid={`package-lease-${p.id}`}>
          project value · <span className="text-primary">{p.custom ? "plan quoted per build" : `then ~${fmt(p.lease_from || Math.round(p.price_from * 0.06) + 12)}/mo`}</span> after 30 free days
        </p>
        <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
        <ul className="mt-6 space-y-2.5 text-sm flex-1">
          {p.features.map((f) => (
            <li key={f} className="flex gap-2.5"><Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /> {f}</li>
          ))}
        </ul>
        <Link
          to={`/start-project?package=${p.id}`}
          data-testid={`package-cta-${p.id}`}
          className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-semibold transition active:scale-95 ${p.featured ? "bg-primary text-white hover:bg-primary/90" : "border border-border hover:border-primary hover:text-primary"}`}
        >
          Choose This Package <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Reveal>
  );
}

function CareCard({ plan, i, origin, monthsCharged }) {
  const { fmt, code } = useCurrency();
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [form, setForm] = useState({ name: "", email: "" });
  const annual = plan.monthly * (monthsCharged || 10);
  const shown = billing === "annual" ? annual : plan.monthly;
  const subscribe = async () => {
    if (!form.name.trim() || !/.+@.+\..+/.test(form.email)) {
      toast.error("Enter your name and email to start your free month.");
      return;
    }
    setBusy(true);
    try {
      const r = await careSubscribe({ plan_id: plan.id, billing, name: form.name, email: form.email, currency: code, origin_url: origin });
      window.location.href = r.checkout_url;
    } catch {
      toast.error("Could not start checkout — please try again or contact me.");
      setBusy(false);
    }
  };
  return (
    <Reveal delay={i * 0.07} className="h-full">
      <div data-testid={`care-${plan.id}`} className={`flex flex-col h-full border p-8 ${plan.featured ? "border-primary bg-card soft-shadow-lg relative" : "border-border bg-card"}`}>
        {plan.featured && <span className="absolute -top-3 left-8 font-mono text-[10px] uppercase tracking-widest bg-primary text-white px-3 py-1">Best value</span>}
        <h3 className="font-display text-2xl">{plan.name}</h3>
        <div className="mt-4 grid grid-cols-2 gap-1 border border-border p-1" data-testid={`care-billing-${plan.id}`}>
          <button onClick={() => setBilling("monthly")} data-testid={`care-billing-monthly-${plan.id}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "monthly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Monthly
          </button>
          <button onClick={() => setBilling("annual")} data-testid={`care-billing-annual-${plan.id}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "annual" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Annual −2 mo
          </button>
        </div>
        <p className="mt-3 font-display text-4xl text-primary">
          {fmt(shown)}<span className="text-lg text-muted-foreground">/{billing === "annual" ? "year" : "month"}</span>
        </p>
        {billing === "annual" && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid={`care-annual-note-${plan.id}`}>
            = {fmt(plan.monthly)}/month · 2 months free
          </p>
        )}
        <p className="mt-1 text-xs font-mono uppercase tracking-widest text-primary" data-testid={`care-trial-${plan.id}`}>First 30 days free</p>
        <p className="mt-2 text-sm text-muted-foreground">{plan.tagline}</p>
        <ul className="mt-6 space-y-2.5 text-sm flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex gap-2.5"><Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /> {f}</li>
          ))}
        </ul>
        <div className="mt-6 space-y-3" data-testid={`care-register-${plan.id}`}>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your name" aria-label="Your name" data-testid={`care-name-${plan.id}`}
            className="w-full bg-secondary px-3.5 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" />
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="Your email" type="email" aria-label="Your email" data-testid={`care-email-${plan.id}`}
            className="w-full bg-secondary px-3.5 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <button
          onClick={subscribe}
          disabled={busy}
          data-testid={`care-cta-${plan.id}`}
          className={`mt-4 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-semibold transition active:scale-95 disabled:opacity-60 ${plan.featured ? "bg-primary text-white hover:bg-primary/90" : "border border-border hover:border-primary hover:text-primary"}`}
        >
          <ShieldCheck className="w-4 h-4" /> {busy ? "Opening checkout…" : "Start My Free Month"}
        </button>
        <p className="mt-3 text-[11px] text-muted-foreground text-center">$0 today · then {fmt(shown)}/{billing === "annual" ? "year" : "month"} after 30 days · cancel anytime</p>
      </div>
    </Reveal>
  );
}

export default function Pricing() {
  const [cfg, setCfg] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [careSuccess, setCareSuccess] = useState(false);
  const [params] = useSearchParams();
  const origin = window.location.origin;

  useEffect(() => {
    getPricingConfig().then(setCfg).catch(() => {});
    getFaqs().then(setFaqs).catch(() => {});
  }, []);

  // Care-plan subscription return handling (30-day free trial: Stripe reports
  // payment_status "no_payment_required" during trials).
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("care") === "success" && sid) {
      paymentStatus(sid).then((s) => {
        if (s.payment_status === "paid" || s.payment_status === "no_payment_required") setCareSuccess(true);
      }).catch(() => setCareSuccess(true)); // checkout itself succeeded; show confirmation regardless
    } else if (params.get("care") === "cancelled") {
      toast.info("Checkout cancelled — no charge was made.");
    }
  }, [params]);

  if (!cfg) return <div className="pt-24"><Spinner /></div>;

  return (
    <div data-testid="pricing-page">
      <SEO
        title="Website Pricing — Transparent Packages & Care Plans | Julius Mason"
        description="Transparent website pricing: Starter from $499, Business from $899, Business Pro from $1,499, custom from $2,500. Optional website care plans from $49/month. Instant online estimates."
        path="/pricing"
        lang="en"
        keywords="website pricing, website cost, website packages, website maintenance plans, web design prices"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }])}
      />
      <PageHeader
        chapter="Pricing / Transparent & Upfront"
        titleLines={["Honest pricing.", "No surprises."]}
        description="Every package is a starting point — your instant estimate tailors it to your exact project. Prices display in your currency; estimates are preliminary and confirmed before work begins."
      />

      {careSuccess && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-[1600px] px-5 md:px-10 mt-8" data-testid="care-success-banner">
          <div className="border border-primary/40 bg-primary/5 p-6 flex items-start gap-4">
            <PartyPopper className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <p className="font-display text-xl">Your free month has started.</p>
              <p className="text-sm text-muted-foreground mt-1">Your first charge only happens after 30 days — cancel anytime before that. I'll email your onboarding details within one business day.</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* PACKAGES */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Website Packages" title="Starting points, not straitjackets." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cfg.packages.map((p, i) => <PackageCard key={p.id} p={p} i={i} />)}
          </div>
          <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
            All packages are starting points — your final quote depends on pages and features.
            No deposit, ever: every project starts with 30 free days, then one simple monthly or annual plan
            that covers hosting, security, backups, updates and care. Cancel anytime.
          </p>
        </div>
      </section>

      {/* QUOTE CALCULATOR */}
      <section className="py-20 md:py-28 border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="max-w-3xl mb-12">
            <SectionTitle overline="Instant Estimate" title="Price your exact project." className="mb-4" />
            <p className="text-muted-foreground text-lg">Answer six quick questions — your estimate updates live as you choose.</p>
          </div>
          <QuoteCalculator compact />
        </div>
      </section>

      {/* CARE PLANS */}
      <section className="py-20 md:py-28 border-t border-border" data-testid="care-plans">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Website Care Plans" title="Your site, looked after." />
          <p className="text-muted-foreground max-w-2xl -mt-6 mb-4 leading-relaxed">
            Completely optional and cancel-anytime. Every plan includes hosting, security, and backups —
            higher tiers add content updates, SEO monitoring, and priority development.
          </p>
          <p className="mb-12 inline-flex items-center gap-2 border border-primary/40 bg-primary/5 px-4 py-2.5 text-sm font-medium" data-testid="care-trial-banner">
            <ShieldCheck className="w-4 h-4 text-primary" /> Every plan starts with a <b>free 30-day trial</b> — register, see the price, pay nothing today.
          </p>
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl">
            {cfg.care_plans.map((p, i) => <CareCard key={p.id} plan={p} i={i} origin={origin} monthsCharged={(cfg.care_config || {}).annual_months_charged || 10} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section id="faq" className="py-20 md:py-28 border-t border-border bg-secondary/20" data-testid="pricing-faq">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionTitle overline="FAQ" title={<>Straight answers.</>} className="mb-6" />
              <p className="text-muted-foreground">Something else on your mind? <Link to="/contact" className="text-primary link-underline">Ask me directly</Link> — I reply personally.</p>
            </div>
            <div className="lg:col-span-8">
              {faqs.map((f, i) => (
                <details key={i} className="group border-b border-border py-5" data-testid={`faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer font-medium text-lg list-none">
                    {f.q}
                    <ArrowRight className="w-4 h-4 text-primary rotate-90 group-open:-rotate-90 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <p className="mt-4 text-muted-foreground leading-relaxed pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Ready when you are.</h2>
          <p className="mt-6 text-primary-foreground/85 text-lg">Get an instant estimate, or start your project online — free for 30 days, no deposit.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/start-project" data-testid="pricing-start-cta" className="inline-flex items-center gap-2 bg-background text-primary px-8 py-4 font-semibold hover:bg-background/90 active:scale-95 transition">
              Start My Project <Zap className="w-5 h-5" />
            </Link>
            <Link to="/free-website-audit" data-testid="pricing-audit-cta" className="inline-flex items-center gap-2 border border-primary-foreground/40 px-8 py-4 font-semibold hover:bg-primary-foreground/10 active:scale-95 transition">
              Get a Free Website Audit <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
